import React from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Statistic, 
  Typography, 
  Space, 
  Button,
  Progress,
  List,
  Avatar,
  Tag,
  Divider,
  Spin,
  Alert
} from 'antd';
import {
  ShoppingCartOutlined,
  ContactsOutlined,
  CalculatorOutlined,
  BoxPlotOutlined,
  ShopOutlined,
  TeamOutlined,
  ToolOutlined,
  BarChartOutlined,
  RiseOutlined,
  FallOutlined,
  DollarOutlined,
  UserOutlined,
  BankOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDashboard } from '../../hooks/useDashboard';

const { Title, Text } = Typography;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  
  // Use real dashboard data
  const {
    loading,
    error,
    salesStats,
    crmStats,
    accountingStats,
    inventoryStats,
    recentActivities,
    refreshDashboard,
    quickActions,
    topPerformers,
    keyMetrics
  } = useDashboard();

  // Data is available immediately from the hook, no need for complex loading logic

  // Provide fallback data structure
  const safeStats = {
    sales: salesStats || {
      totalRevenue: 145750,
      monthlyGrowth: 12.5,
      ordersCount: 28,
      quotationsCount: 15,
      invoicesCount: 22,
      recentOrders: []
    },
    crm: crmStats || {
      totalCustomers: 156,
      activeCustomers: 89,
      conversionRate: 57.05,
      pipelineValue: 218625,
      recentCustomers: []
    },
    accounting: accountingStats || {
      totalRevenue: 145750,
      monthlyProfit: 21862.5,
      cashFlow: 14575
    },
    inventory: inventoryStats || {
      totalProducts: 245,
      lowStockItems: 8,
      stockValue: 89340,
      warehouseCount: 3,
      lowStockDetails: []
    },
    key: keyMetrics || {
      totalRevenue: 145750,
      monthlyGrowth: 12.5,
      totalCustomers: 156,
      totalProducts: 245,
      lowStockAlerts: 8,
      pendingOrders: 28
    }
  };

  // No loading screen needed - data is available immediately

  // Icon mapping for quick actions
  const iconMap = {
    ShoppingCart: <ShoppingCartOutlined />,
    Contacts: <ContactsOutlined />,
    Calculator: <CalculatorOutlined />,
    Package: <BoxPlotOutlined />,
    Shop: <ShopOutlined />,
    Team: <TeamOutlined />
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'sale': return <ShoppingCartOutlined style={{ color: '#1890ff' }} />;
      case 'crm': return <ContactsOutlined style={{ color: '#52c41a' }} />;
      case 'accounting': return <CalculatorOutlined style={{ color: '#fa8c16' }} />;
      case 'inventory': return <BoxPlotOutlined style={{ color: '#722ed1' }} />;
      case 'purchase': return <ShopOutlined style={{ color: '#eb2f96' }} />;
      default: return <ClockCircleOutlined />;
    }
  };

  return (
    <div style={{ padding: '0' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ margin: 0, color: '#011f4b' }}>
            Welcome User
          </Title>
          <Text type="secondary">
            Here's what's happening with your business today
          </Text>
          {error && (
            <Alert
              message="Connection Issue"
              description="Unable to connect to server. Showing sample data."
              type="warning"
              showIcon
              closable
              style={{ marginTop: 16 }}
            />
          )}
        </div>

        {/* Key Metrics - Mobile Responsive */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Revenue"
                value={safeStats.sales.totalRevenue}
                precision={0}
                prefix={<DollarOutlined />}
                suffix={
                  <span style={{ fontSize: '12px', color: '#52c41a' }}>
                    <ArrowUpOutlined /> {safeStats.sales.monthlyGrowth}%
                  </span>
                }
                valueStyle={{ color: '#3f8600', fontSize: window.innerWidth < 768 ? '20px' : '24px' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Active Customers"
                value={safeStats.crm.totalCustomers}
                prefix={<TrophyOutlined />}
                suffix={
                  <span style={{ fontSize: '12px' }}>
                    {safeStats.crm.conversionRate}% conversion
                  </span>
                }
                valueStyle={{ color: '#1890ff', fontSize: window.innerWidth < 768 ? '20px' : '24px' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Revenue This Month"
                value={safeStats.accounting.totalRevenue}
                precision={0}
                prefix={<BankOutlined />}
                valueStyle={{ color: '#722ed1', fontSize: window.innerWidth < 768 ? '20px' : '24px' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Products in Stock"
                value={safeStats.inventory.totalProducts}
                prefix={<BoxPlotOutlined />}
                suffix={
                  safeStats.inventory.lowStockItems > 0 && (
                    <span style={{ fontSize: '12px', color: '#fa541c' }}>
                      {safeStats.inventory.lowStockItems} low stock
                    </span>
                  )
                }
                valueStyle={{ color: '#fa8c16', fontSize: window.innerWidth < 768 ? '20px' : '24px' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Module Overview */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Sales Overview" extra={<Button type="link" onClick={() => navigate('/sales')}>View All</Button>}>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Orders"
                    value={salesStats.ordersCount}
                    valueStyle={{ fontSize: '24px' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Quotations"
                    value={salesStats.quotationsCount}
                    valueStyle={{ fontSize: '24px' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Invoices"
                    value={salesStats.invoicesCount}
                    valueStyle={{ fontSize: '24px' }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="CRM Performance" extra={<Button type="link" onClick={() => navigate('/crm')}>View All</Button>}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text>Lead Conversion Rate</Text>
                  <Progress 
                    percent={crmStats.conversionRate} 
                    strokeColor="#52c41a"
                    format={percent => `${percent}%`}
                  />
                </div>
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title="Total Leads"
                      value={crmStats.totalLeads}
                      valueStyle={{ fontSize: '20px' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Qualified"
                      value={crmStats.qualifiedLeads}
                      valueStyle={{ fontSize: '20px', color: '#52c41a' }}
                    />
                  </Col>
                </Row>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Quick Actions */}
        <Card title="Quick Actions">
          <Row gutter={[16, 16]}>
            {quickActions.map((action, index) => (
              <Col xs={12} sm={8} md={4} key={index}>
                <Card
                  hoverable
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                  onClick={() => navigate(action.path)}
                  bodyStyle={{ padding: '16px' }}
                >
                  <div style={{ fontSize: '24px', color: action.color, marginBottom: '8px' }}>
                    {action.icon}
                  </div>
                  <Text strong style={{ fontSize: '12px' }}>{action.title}</Text>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>

        <Row gutter={[16, 16]}>
          {/* Recent Activities */}
          <Col xs={24} lg={14}>
            <Card title="Recent Activities" extra={<Button type="link">View All</Button>}>
              <List
                itemLayout="horizontal"
                dataSource={recentActivities}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={getActivityIcon(item.type)}
                      title={item.description}
                      description={
                        <Space>
                          <Text type="secondary">{item.time}</Text>
                          <Tag color="blue">{item.amount}</Tag>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          {/* Top Performers */}
          <Col xs={24} lg={10}>
            <Card title="Top Performers" extra={<Button type="link">View All</Button>}>
              <List
                itemLayout="horizontal"
                dataSource={topPerformers}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar style={{ backgroundColor: '#1890ff' }}>{item.avatar}</Avatar>}
                      title={item.name}
                      description={item.role}
                    />
                    <div>
                      <Progress
                        type="circle"
                        percent={item.performance}
                        width={50}
                        strokeColor="#52c41a"
                      />
                    </div>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>

        {/* Financial Summary */}
        <Card title="Financial Summary" extra={<Button type="link" onClick={() => navigate('/accounting')}>View Details</Button>}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Total Assets"
                  value={accountingStats.totalAssets}
                  precision={0}
                  prefix="$"
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Total Liabilities"
                  value={accountingStats.totalLiabilities}
                  precision={0}
                  prefix="$"
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Monthly Profit"
                  value={accountingStats.monthlyProfit}
                  precision={0}
                  prefix="$"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
          </Row>
        </Card>
      </Space>
    </div>
  );
};

export default Dashboard; 